import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import { useTheme } from "@/contexts/ThemeContext";
import { useDashboard } from "@/contexts/DashboardContext";
import { useAuth } from "@/contexts/AuthContext";
import { SyncStatusIndicator } from "@/components/dashboard/SyncStatus";
import { LanguageSelector } from "@/components/dashboard/CommonComponents";
import { Button } from "@/components/ui/button";
import { API } from "@/components/dashboard/constants";
import {
  BarChart3,
  Search,
  Target,
  TrendingUp,
  Eye,
  MessageSquare,
  Globe,
  Sparkles,
  Settings,
  Users,
  LayoutDashboard,
  Menu,
  X,
  Sun,
  Moon,
  BookOpen,
  RefreshCw,
  Shield,
  FlaskConical,
  Zap,
  FileText,
} from "lucide-react";

// Route configuration
const ROUTES = [
  { path: "/", id: "overview", icon: LayoutDashboard, labelKey: "dashboard" },
  { path: "/accounts", id: "accounts", icon: Users, labelKey: "accounts" },
  { path: "/follower-growth", id: "follower-growth", icon: TrendingUp, labelKey: "followerGrowth" },
  { path: "/posts", id: "posts", icon: Eye, labelKey: "posts" },
  { path: "/content-growth", id: "content-growth", icon: BarChart3, labelKey: "contentGrowth" },
  { path: "/comments", id: "comments", icon: MessageSquare, labelKey: "comments" },
  { path: "/comment-search", id: "comment-search", icon: Search, labelKey: "commentSearch" },
  { path: "/keyword-search", id: "keyword-search", icon: Search, labelKey: "keywordSearch" },
  { path: "/competitor-tracking", id: "competitor-tracking", icon: Target, labelKey: "competitorTracking" },
  { path: "/comparison", id: "comparison", icon: Globe, labelKey: "crossPlatform" },
  { path: "/insights", id: "insights", icon: Sparkles, labelKey: "insights" },
  { path: "/correlation", id: "correlation", icon: FlaskConical, labelKey: "correlation", adminOnly: true },
  { path: "/spillover", id: "spillover", icon: Zap, labelKey: "spillover", adminOnly: true },
  { path: "/reports", id: "reports", icon: FileText, labelKey: "reports", adminOnly: true },
  { path: "/guide", id: "guide", icon: BookOpen, labelKey: "guide" },
  { path: "/sync-logs", id: "sync-logs", icon: RefreshCw, labelKey: "syncLogs" },
  { path: "/settings", id: "settings", icon: Settings, labelKey: "settings", adminOnly: true, section: "settings" },
  { path: "/settings/artist-groups", id: "artist-groups", icon: Users, labelKey: "artistGroups", adminOnly: true, section: "settings", nested: true },
];

// Labels by language
const ROUTE_LABELS = {
  en: {
    dashboard: "Dashboard",
    accounts: "Accounts",
    keywordSearch: "Keyword Search",
    competitorTracking: "Competitor Tracking",
    followerGrowth: "Follower Growth",
    contentGrowth: "Content Growth",
    posts: "Posts",
    comments: "Comments",
    commentSearch: "Comment Search",
    crossPlatform: "Cross-Platform",
    syncLogs: "Sync Logs",
    insights: "AI Insights",
    correlation: "Correlation",
    artistGroups: "Artist Groups",
    spillover: "Spillover Analysis",
    reports: "Reports",
    guide: "User Guide",
    settings: "Settings",
  },
  ko: {
    dashboard: "대시보드",
    accounts: "계정",
    keywordSearch: "키워드 검색",
    competitorTracking: "경쟁사 트래킹",
    followerGrowth: "팔로워 추이",
    contentGrowth: "콘텐츠 성장",
    posts: "게시물",
    comments: "댓글",
    commentSearch: "댓글 검색",
    crossPlatform: "크로스플랫폼",
    syncLogs: "동기화 로그",
    insights: "AI 인사이트",
    correlation: "상관관계 분석",
    artistGroups: "아티스트 그룹",
    spillover: "스필오버 분석",
    reports: "리포트",
    guide: "사용 가이드",
    settings: "설정",
  },
  zh: {
    dashboard: "仪表板",
    accounts: "账户",
    keywordSearch: "关键词搜索",
    competitorTracking: "竞品追踪",
    followerGrowth: "粉丝增长",
    contentGrowth: "内容增长",
    posts: "帖子",
    comments: "评论",
    commentSearch: "评论搜索",
    crossPlatform: "跨平台",
    syncLogs: "同步日志",
    insights: "AI洞察",
    correlation: "相关性分析",
    artistGroups: "艺人分组",
    spillover: "溢出效应分析",
    reports: "报告",
    guide: "用户指南",
    settings: "设置",
  },
};

const DashboardLayout = () => {
  const { theme, toggleTheme, isDark } = useTheme();
  const { lang, setLang, summary, refreshData } = useDashboard();
  const { isAdmin, user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [featureAccess, setFeatureAccess] = useState(null);
  const location = useLocation();
  
  // Fetch feature access settings
  useEffect(() => {
    if (!isAdmin) {
      axios.get(`${API}/settings/feature-access`)
        .then(res => setFeatureAccess(res.data.features || {}))
        .catch(() => setFeatureAccess(null));
    }
  }, [isAdmin]);
  
  const labels = ROUTE_LABELS[lang] || ROUTE_LABELS.en;
  
  // Filter routes: admins see everything, non-admins respect featureAccess
  const visibleRoutes = ROUTES.filter(route => {
    if (isAdmin) return true;
    // Settings and artist-groups are always admin-only
    if (route.section === "settings") return false;
    // Check feature access from DB
    if (featureAccess && route.id in featureAccess) {
      return featureAccess[route.id];
    }
    // If no featureAccess loaded yet, fall back to original adminOnly logic
    if (route.adminOnly) return false;
    return true;
  });
  const primaryRoutes = visibleRoutes.filter((route) => route.section !== "settings");
  const settingsRoutes = visibleRoutes.filter((route) => route.section === "settings");
  
  // Get current page title
  const getCurrentPageTitle = () => {
    const currentRoute = ROUTES.find(r => r.path === location.pathname);
    if (currentRoute) {
      return labels[currentRoute.labelKey];
    }
    return labels.dashboard;
  };

  return (
    <div className="flex min-h-[100dvh] bg-background">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-full bg-card border-r border-border/50 flex flex-col">
          {/* Logo */}
          <div className="p-4 border-b border-border/50">
            <NavLink to="/" className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg text-foreground">A2O Analytics</h1>
                <p className="text-xs text-muted-foreground">SNS Dashboard</p>
              </div>
            </NavLink>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {primaryRoutes.map((route) => (
              <NavLink
                key={route.id}
                to={route.path}
                end={route.path === "/"}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `sidebar-item w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm transition-colors ${
                    isActive
                      ? "active text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`
                }
                data-testid={`nav-${route.id}`}
              >
                <route.icon className="h-4 w-4" />
                {labels[route.labelKey]}
              </NavLink>
            ))}

            {settingsRoutes.length ? (
              <div className="pt-3" data-testid="nav-settings-section">
                <p className="px-4 pb-2 text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground/60">{labels.settings}</p>
                <div className="space-y-1">
                  {settingsRoutes.map((route) => (
                    <NavLink
                      key={route.id}
                      to={route.path}
                      end={route.path === "/settings"}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `sidebar-item w-full flex items-center gap-3 rounded-md text-sm transition-colors ${route.nested ? "px-6 py-2.5 ml-2" : "px-4 py-3"} ${
                          isActive
                            ? "active text-primary bg-primary/10"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        }`
                      }
                      data-testid={`nav-${route.id}`}
                    >
                      <route.icon className="h-4 w-4" />
                      {labels[route.labelKey]}
                    </NavLink>
                  ))}
                </div>
              </div>
            ) : null}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-border/50 space-y-3">
            {/* User Role Badge */}
            <div className="flex min-w-0 items-center gap-2 rounded-md bg-muted/30 px-2 py-1.5">
              <Shield className={`h-3.5 w-3.5 ${isAdmin ? 'text-primary' : 'text-muted-foreground'}`} />
              <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">{user?.name || ''}</span>
              <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isAdmin ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                {isAdmin ? (lang === 'ko' ? '관리자' : lang === 'zh' ? '管理员' : 'Admin') : (lang === 'ko' ? '사용자' : lang === 'zh' ? '用户' : 'User')}
              </span>
            </div>
            {/* Theme Toggle */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {lang === 'ko' ? '테마' : lang === 'zh' ? '主题' : 'Theme'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="h-8 w-8 p-0"
                data-testid="theme-toggle"
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>
            <div className="text-xs text-muted-foreground/50">
              <p>Last updated</p>
              <p className="font-mono">{new Date().toLocaleTimeString()}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-wrap items-start justify-between gap-3 sm:items-center sm:gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-2 sm:items-center sm:gap-4">
              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 hover:bg-muted rounded-md flex-shrink-0"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="menu-toggle"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              
              <div className="min-w-0 flex-1">
                <h2 className="font-heading font-bold text-base sm:text-xl text-foreground capitalize truncate">
                  {getCurrentPageTitle()}
                </h2>
                <p className="text-xs sm:text-sm text-muted-foreground break-words">
                  {summary?.total_posts || 0} {lang === 'ko' ? '게시물' : lang === 'zh' ? '帖子' : 'posts'} • {summary?.total_accounts || 0} {lang === 'ko' ? '계정' : lang === 'zh' ? '账户' : 'accounts'}
                </p>
              </div>
              
              {/* Sync Status */}
              <div className="hidden sm:block">
                <SyncStatusIndicator
                  onSyncComplete={() => refreshData()}
                  lang={lang}
                  showToasts={true}
                />
              </div>
            </div>

            {/* Language Selector */}
            <div className="flex w-full items-center justify-end gap-2 sm:w-auto sm:justify-start">
              <LanguageSelector lang={lang} setLang={setLang} />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="min-w-0 overflow-x-hidden p-4 sm:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
